<script lang="ts" setup>
import AppButton from "@/components/atoms/button/AppButton.vue";
import ToggleButton from "@/components/atoms/button/ToggleButton.vue";
import AppFormSelect from "@/components/atoms/input/AppFormSelect.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import CustomValidationObserver from "@/plugins/CustomValidationObserver.vue";
import { ValidationProvider } from "@/plugins/veeValidate/veeValidate";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import {
  addDays,
  combineDateAndTime,
  formatFromISOString,
  formatFromUnixTimestamp,
  getDate,
  getHour,
  now,
  parseISO
} from "@/utils/date";
import { computed, nextTick, ref, watch } from "vue";

const props = defineProps<{
  visible: boolean;
  selectedDate: string | null;
  selectedSchedules: DesiredWorkSchedule[];
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (
    e: "save",
    schedule: {
      date: string;
      isAllDay: boolean;
      startAt: string | null;
      finishAt: string | null;
    }
  ): void;
  (e: "delete", date: string): void;
}>();

const isAllDay = ref<boolean>(false);
const startAt = ref<string | null>(null);
const finishAt = ref<string | null>(null);

const formValues = computed(() => ({
  date: props.selectedDate,
  isAllDay: isAllDay.value,
  startAt: startAt.value,
  finishAt: finishAt.value
}));

const existsSchedule = computed(() => {
  return props.selectedSchedules.some(
    schedule => schedule.date === props.selectedDate
  );
});

const getPadHHmm = (hour: number) => {
  return `${hour.toString().padStart(2, "0")}:00`;
};

const formatTime = (date: number, time: number) => {
  const formattedTime = formatFromUnixTimestamp(
    combineDateAndTime(date, getPadHHmm(time)),
    "yyyy-MM-dd'T'HH:mm:ss+09:00"
  );
  return formattedTime;
};

const startAtOptions = computed(() => {
  const targetDate = props.selectedDate ? parseISO(props.selectedDate) : now();
  return [
    ...Array.from({ length: 24 }, (_, hour) => {
      return {
        text: getPadHHmm(hour),
        value: formatTime(targetDate, hour)
      };
    })
  ];
});

const finishAtOptions = computed(() => {
  const fromHour = formValues.value.startAt
    ? getHour(parseISO(formValues.value.startAt!))
    : null;
  const targetDate = props.selectedDate ? parseISO(props.selectedDate) : now();
  return [
    ...Array.from({ length: 36 })
      .map((_, index) => {
        return index + 1;
      })
      .filter(hour => {
        if (!fromHour) {
          return true;
        }
        return hour > fromHour;
      })
      .map(hour => {
        const upwardHour = hour < 24 ? hour : hour - 24;
        return {
          text:
            hour < 24 ? getPadHHmm(upwardHour) : `翌${getPadHHmm(upwardHour)}`,
          value:
            hour < 24
              ? formatTime(targetDate, upwardHour)
              : formatTime(addDays(targetDate, 1), upwardHour)
        };
      })
  ];
});

/**
 * 選択された日付に基づいてフォームのデフォルト値を設定する
 */
const setDefaultFormValues = () => {
  const targetSchedule = props.selectedSchedules.find(
    schedule => schedule.date === props.selectedDate
  );
  if (targetSchedule) {
    // 選択した日に既存の働きたい日がある場合は、その日の値をフォームのデフォルト値とする
    isAllDay.value = targetSchedule.isAllDay;
    startAt.value = targetSchedule.startAt || null;
    // モーダルの表示が更新されるまで待ってからfinishAtを更新する
    nextTick(() => {
      finishAt.value = targetSchedule.finishAt || null;
    });
  } else {
    // 選択した日に既存の働きたい日がない場合は、1つ前に選択した時のフォームの値をフォームのデフォルト値とする
    if (props.selectedDate && startAt.value && finishAt.value) {
      const parsedSelectedDate = parseISO(props.selectedDate);
      const parsedSelectedDateTomorrow = addDays(parsedSelectedDate, 1);
      const parsedStartAt = parseISO(startAt.value);
      const parsedFinishAt = parseISO(finishAt.value);
      // 選択した日付の選択肢とマッチするように、日付部分を更新する
      startAt.value = formatTime(parsedSelectedDate, getHour(parsedStartAt));
      // モーダルの表示が更新されるまで待ってからfinishAtを更新する
      nextTick(() => {
        finishAt.value = formatTime(
          // 終了日が日付を跨いでいた場合は、翌日の値をデフォルト値とする
          getDate(parsedStartAt) === getDate(parsedFinishAt)
            ? parsedSelectedDate
            : parsedSelectedDateTomorrow,
          getHour(parsedFinishAt)
        );
      });
    }
  }
};

watch(
  () => props.selectedDate,
  () => {
    setDefaultFormValues();
  }
);

watch(
  () => formValues.value.startAt,
  () => {
    finishAt.value = null;
  }
);

const closeBottomSheet = () => {
  emit("update:visible", false);
};

const toggleAllDay = () => {
  isAllDay.value = !isAllDay.value;
  startAt.value = null;
  finishAt.value = null;
};

const formatDate = (date: string | null) => {
  if (!date) return "";
  return formatFromISOString(date, "Y年M月d日 iiiii曜日");
};

const onClickSave = () => {
  if (!formValues.value.date) {
    return;
  }

  emit("save", {
    date: formValues.value.date,
    isAllDay: formValues.value.isAllDay,
    startAt: formValues.value.startAt,
    finishAt: formValues.value.finishAt
  });
};

const onClickDelete = () => {
  if (!formValues.value.date) {
    return;
  }

  emit("delete", formValues.value.date);
};

defineExpose({
  isAllDay,
  startAt,
  finishAt,
  setDefaultFormValues,
  toggleAllDay,
  formatDate,
  onClickSave,
  onClickDelete,
  closeBottomSheet
});
</script>

<template>
  <BModal
    :visible="visible"
    hide-footer
    hide-header
    modal-class="schedule-form-bottom-sheet"
    @hide="closeBottomSheet"
    @close="closeBottomSheet"
  >
    <CustomValidationObserver
      :initial-values="formValues"
      v-slot="{ handleSubmit }"
    >
      <div class="d-flex align-items-center">
        <p class="bottom-sheet-header">{{ formatDate(selectedDate) }}</p>
        <span class="ml-auto">
          <FeatherIcon icon="x" size="24" @click="closeBottomSheet" />
        </span>
      </div>
      <div>
        <div class="d-flex align-items-center all-day-wrapper">
          <div class="toggle-button-wrapper">
            <ToggleButton
              :checked="isAllDay"
              @click="toggleAllDay"
              data-testid="isAllDay-toggle"
            />
          </div>
          <p>終日</p>
        </div>
        <div class="d-flex align-items-center mb-4">
          <ValidationProvider
            name="開始時刻"
            :rules="{
              required: !formValues.isAllDay
            }"
            v-slot="validationState"
            vid="startAt"
          >
            <div class="d-flex align-items-center">
              <span
                class="mr-2"
                :class="{ 'disabled-time-option': formValues.isAllDay }"
                >開始</span
              >
              <AppFormSelect
                :value="startAt"
                v-model="startAt"
                firstOption="選択して下さい"
                :formOptions="startAtOptions"
                :disabled="formValues.isAllDay"
                class="time-option"
                :validation-state="validationState"
                data-testid="startAt-select"
              />
            </div>
          </ValidationProvider>

          <ValidationProvider
            name="終了時刻"
            :rules="{
              required: !formValues.isAllDay
            }"
            v-slot="validationState"
            vid="finishAt"
          >
            <div class="d-flex align-items-center">
              <span
                class="m-2"
                :class="{ 'disabled-time-option': formValues.isAllDay }"
                >終了</span
              >
              <AppFormSelect
                :value="finishAt"
                v-model="finishAt"
                firstOption="選択して下さい"
                :formOptions="finishAtOptions"
                :disabled="formValues.isAllDay"
                class="time-option"
                :validation-state="validationState"
                data-testid="finishAt-select"
              />
            </div>
          </ValidationProvider>
        </div>
        <div class="d-flex flex-column align-items-center">
          <AppButton @click="handleSubmit(onClickSave)" class="save-button">
            登録する
          </AppButton>

          <div class="my-4">
            <button
              class="delete-button"
              :disabled="!existsSchedule"
              @click="handleSubmit(onClickDelete)"
            >
              削除する
            </button>
          </div>
        </div>
      </div>
    </CustomValidationObserver>
  </BModal>
</template>

<style lang="scss">
.schedule-form-bottom-sheet {
  .modal-dialog {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1050;
    width: 100vw;
    margin: 0;
  }

  .modal-content {
    border: none;
    border-radius: 16px 16px 0 0;
  }
}

.modal.fade .modal-dialog {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(100%);
}

.modal.fade.show .modal-dialog {
  transform: translateY(0);
}

.modal.fade.show {
  padding: 0 !important;
}
</style>

<style lang="scss" scoped>
.bottom-sheet-header {
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
}

.all-day-wrapper {
  margin-bottom: 16px;
}

.toggle-button-wrapper {
  margin-right: 8px;
}

.time-option {
  width: 127.5px;
}

.disabled-time-option {
  color: $gray-05;
}

.save-button {
  width: 200px;
}

.delete-button {
  color: $primary;

  &:disabled {
    color: $gray-05;
  }
}
</style>
